/**
 * MeuPolitico — Funcionalidade 3 (Fase 2)
 *
 * Public page /meu-politico: search a deputy by name, then see a
 * humanised summary of expenses / categories / proposals for the
 * current year. Uses the Câmara dos Deputados open-data API.
 */
import React, { useState, useCallback } from "react";
import ExplainButton from "../components/ExplainButton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const brl = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

export default function MeuPolitico() {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resumo, setResumo] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [err, setErr] = useState(null);

  const searchDeputados = useCallback(async () => {
    if (!query || query.length < 2) return;
    setSearching(true); setResumo(null); setErr(null);
    try {
      const r = await fetch(`${BACKEND}/api/public/camara/parlamentares?nome=${encodeURIComponent(query)}`);
      const j = await r.json();
      setSuggestions(j.items || []);
      if (!j.items || j.items.length === 0) setErr("no_match");
    } catch {
      setErr("network");
    } finally {
      setSearching(false);
    }
  }, [query]);

  const loadResumo = async (dep) => {
    setLoadingResumo(true); setErr(null); setSuggestions([]);
    try {
      const r = await fetch(`${BACKEND}/api/public/parlamentar/${dep.id}/resumo`);
      if (!r.ok) throw new Error("not_found");
      const j = await r.json();
      setResumo(j);
    } catch {
      setErr("resumo_fail");
    } finally {
      setLoadingResumo(false);
    }
  };

  const shareWhatsApp = () => {
    if (!resumo) return;
    const p = resumo.parlamentar;
    const g = resumo.gastos;
    const site = typeof window !== "undefined" ? window.location.origin : BACKEND;
    const text =
      `🏛️ Sabia que ${p.nome} (${p.partido || "?"}) gastou ${brl(g.total)} com o mandato em ${g.ano}?\n` +
      `A maior parte foi com ${g.maior_categoria?.categoria || "outras despesas"}.\n` +
      `Confira no Tô de Olho:\n${site}/meu-politico`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="meu-politico-page">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-[#DC2626] mb-2">Meu Político em 1 Minuto</p>
        <h1 className="text-3xl md:text-4xl font-black text-slate-800 mb-1">O que seu deputado anda fazendo?</h1>
        <p className="text-sm text-slate-600">Digite o nome de um(a) deputado(a) federal e veja um resumo rápido dos gastos e projetos do mandato atual.</p>
      </header>

      <section className="bg-white rounded-2xl shadow-md p-6 mb-6">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">Nome do parlamentar</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchDeputados()}
            placeholder="Ex: Erika Kokay"
            className="flex-1 px-4 py-3 border border-slate-300 rounded-lg min-h-[44px] focus:outline-none focus:ring-2 focus:ring-[#DC2626]/30"
            data-testid="politico-input"
          />
          <button
            type="button"
            onClick={searchDeputados}
            disabled={searching}
            className="bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-slate-400 text-white font-semibold px-6 py-3 rounded-lg min-h-[44px] transition-colors"
            data-testid="politico-search-btn"
          >
            {searching ? "Buscando…" : "Buscar"}
          </button>
        </div>

        {suggestions.length > 0 && (
          <ul className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="politico-suggestions">
            {suggestions.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => loadResumo(s)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 min-h-[44px]"
                  data-testid={`politico-suggestion-${s.id}`}
                >
                  {s.foto ? (
                    <img src={s.foto} alt={s.nome} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-bold">?</div>
                  )}
                  <div>
                    <p className="font-semibold text-slate-800">{s.nome}</p>
                    <p className="text-xs text-slate-500">{s.partido || "?"} · {s.uf || "?"}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {loadingResumo && (
        <div className="text-center py-10 text-slate-600" data-testid="politico-loading">Consultando a Câmara dos Deputados…</div>
      )}

      {err && !loadingResumo && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm" data-testid="politico-error">
          {err === "no_match" && "Não encontramos deputado(a) com esse nome. Tente escrever de outra forma."}
          {err === "resumo_fail" && "Ops! A Câmara está lenta agora. Tente em alguns instantes."}
          {err === "network" && "Ops! Não conseguimos consultar agora. Tente em alguns instantes."}
        </div>
      )}

      {resumo && !loadingResumo && (
        <>
          <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" data-testid="politico-result">
            <div className="p-6 flex items-start gap-4 bg-gradient-to-br from-[#DC2626] to-[#7f1d1d] text-white">
              {resumo.parlamentar.foto ? (
                <img src={resumo.parlamentar.foto} alt={resumo.parlamentar.nome} className="w-20 h-20 rounded-full object-cover border-2 border-white flex-shrink-0" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-white/20 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <h2 className="text-xl md:text-2xl font-black leading-tight" data-testid="politico-nome">{resumo.parlamentar.nome}</h2>
                <p className="text-sm text-white/90 mt-1">{resumo.parlamentar.partido || "?"} · {resumo.parlamentar.uf || "?"}</p>
                <p className="text-xs text-white/70">{resumo.parlamentar.cargo}</p>
              </div>
            </div>

            <div className="divide-y divide-slate-100">
              {/* Gastos */}
              <div className="p-6" data-testid="politico-card-gastos">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2 font-semibold">💸 Quanto gastou em {resumo.gastos.ano}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-3xl font-black text-slate-800">{brl(resumo.gastos.total)}</p>
                  <ExplainButton
                    contexto={`gastos do parlamentar ${resumo.parlamentar.nome} com o mandato em ${resumo.gastos.ano}`}
                    valor={brl(resumo.gastos.total)}
                    tipo="valor"
                    fonte="Câmara dos Deputados"
                    entidade={resumo.parlamentar.nome}
                    testId="explain-politico-gastos"
                  />
                </div>
                {resumo.gastos.maior_categoria && (
                  <p className="text-sm text-slate-600 mt-2">
                    A maior parte foi com <strong>{resumo.gastos.maior_categoria.categoria.toLowerCase()}</strong> ({brl(resumo.gastos.maior_categoria.valor)}).
                  </p>
                )}
                {resumo.gastos.por_categoria.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs text-[#006030] cursor-pointer font-semibold">Ver 5 maiores categorias</summary>
                    <ul className="mt-2 space-y-1">
                      {resumo.gastos.por_categoria.map((c, i) => (
                        <li key={i} className="text-xs flex justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-700">{c.categoria}</span>
                          <span className="font-semibold text-slate-800">{brl(c.valor)}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>

              {/* Proposições */}
              <div className="p-6" data-testid="politico-card-proposicoes">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2 font-semibold">📋 O que propôs em {resumo.proposicoes.ano}</p>
                <p className="text-2xl font-black text-slate-800">{resumo.proposicoes.total} <span className="text-sm font-normal text-slate-500">projeto{resumo.proposicoes.total === 1 ? "" : "s"} apresentado{resumo.proposicoes.total === 1 ? "" : "s"}</span></p>
                {resumo.proposicoes.recentes.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {resumo.proposicoes.recentes.map((p, i) => (
                      <li key={i} className="text-sm text-slate-700 border-l-2 border-[#DC2626] pl-3">
                        <span className="font-semibold text-[#DC2626] mr-2">{p.tipo}</span>{p.ementa}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          <button
            type="button"
            onClick={shareWhatsApp}
            className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white font-bold py-3 rounded-lg min-h-[44px] transition-colors mb-4"
            data-testid="politico-share-whatsapp"
          >
            Compartilhar no WhatsApp
          </button>
        </>
      )}
    </div>
  );
}
